import { useQueries, useQuery } from '@tanstack/react-query';
import { LoadingNote } from '@/components/OfflineDetails';
import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import {
  ActionBar,
  ColumnToggle,
  DataGrid,
  Navigator,
  readStored,
  SplitView,
  StatusBar,
  writeStored,
  type ColumnOption,
  type DesktopAction,
  type GridColumn,
  type NavNode,
} from '@/components/desktop';
import { BulkActionDialog, type BulkActionKind } from '@/components/stock-actions/BulkActionDialog';
import { StockActionDialog, type StockActionKind } from '@/components/stock-actions/StockActionDialog';
import { DemandDialog } from '@/components/stock-actions/DemandDialog';
import { AppIcon } from '@/components/AppIcon';
import { DocumentFrame } from '@/components/DocumentFrame';
import { BinSelect } from '@/components/BinSelect';
import { ErrorNote, OkNote, Pager, SearchBar, Select, Table, Tile, money, quantity, rate, shortDate } from '@/components/ui';
import { useItemsById, useStores } from '@/hooks/inventory';
import { contentTotal, packedBreakdown, type PackedItem } from '@/packing';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { useScreenIntent, useShellNav, type ScreenIntent } from '@/stores/shellNav';

// v3: Avg cost shows by default again. Only those allowed to see costs (the
// MD and the Accountant) get the column at all, and for them it is the point
// of the screen; a fresh key drops the saved "hidden" from v2.
const HIDDEN_COLUMNS_KEY = 'poultry.grid.workbench-stock.hidden.v3';
const DEFAULT_HIDDEN_COLUMNS: string[] = [];

type StockRow = components['schemas']['StockBalanceItemDto'];
type StoreRow = components['schemas']['StoreDto'];

const PAGE_SIZE = 100;

type PageTab = 'stockcard' | 'lots' | 'elsewhere';

/** What the navigator's selection means, encoded in the node id. */
interface NavSelection {
  storeId: string | null;
  view: 'all' | 'low' | 'lots' | 'bins';
  /** Under the bins view: one bin's id, `'none'` for loose stock, `''` for every bin. */
  binId: string;
}

function parseNode(id: string): NavSelection {
  if (id === 'all') return { storeId: null, view: 'all', binId: '' };
  if (id === 'low') return { storeId: null, view: 'low', binId: '' };
  const [, storeId, view, binId] = id.split(':');
  return { storeId: storeId ?? null, view: (view as NavSelection['view']) ?? 'all', binId: binId ?? '' };
}

/** Nothing left at all: the most urgent kind of running short. */
/** "2 Cartons + 38 Packs · 138 KG": a quantity as packed and as weighed, whichever the item has. */
function packedText(qty: string, item: PackedItem): string | null {
  const parts = [packedBreakdown(qty, item), contentTotal(qty, item, (v, p) => quantity(v, p))].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

function isOutOfStock(row: StockRow): boolean {
  return Number(row.qty) <= 0;
}

/** A row standing for an item across every store (groupBy=sku), not one store's stock. */
function isGroupRow(row: StockRow): boolean {
  return isGroupKey(row.id);
}

function isGroupKey(key: string): boolean {
  return key.startsWith('sku:');
}

/** The item a ticked row is for: `sku:<item>` for a group, `<store>:<item>` for one store's row. */
function skuOfKey(key: string): string {
  return isGroupKey(key) ? key.slice(4) : (key.split(':')[1] ?? '');
}

function binNode(storeId: string, binId: string): string {
  return binId ? `store:${storeId}:bins:${binId}` : `store:${storeId}:bins`;
}

/** Everything held, or only what is running short, in one store or all of them. */
function stockNode(storeId: string | null, view: 'all' | 'low'): string {
  return storeId ? `store:${storeId}:${view}` : view;
}

/** Where the screen opens: on what another screen asked for, or on all stock. */
function initialNode(intent: ScreenIntent | null): string {
  return stockNode(intent?.filterStoreId ?? null, intent?.view === 'low' ? 'low' : 'all');
}

/**
 * The inventory workbench: pick a store on the left, see what it holds in the
 * middle, and work on the selected row underneath.
 *
 * This is where a storekeeper spends the day, so every stock action starts
 * here rather than on a screen of its own — receiving, issuing, transferring
 * and adjusting all open prefilled with the row you were looking at.
 */
export function Workbench() {
  const { can } = useAuth();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const intent = useScreenIntent('inv-workbench');

  const [node, setNode] = useState(() => initialNode(intent));
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** The row open on its own screen, as it was when opened; kept fresh from the list while it is still on it. */
  const [openRow, setOpenRow] = useState<StockRow | null>(null);
  const [pageTab, setPageTab] = useState<PageTab>('stockcard');
  const [stockAction, setStockAction] = useState<{ kind: StockActionKind; row: StockRow } | null>(null);
  const [demandRow, setDemandRow] = useState<StockRow | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkActionKind | null>(null);
  /** Items opened in the All stores view, by item id. */
  // Sent here for one item ("See its stock" in the Items Manager): only that
  // item, already open to show the stores holding it.
  const [skuFilter, setSkuFilter] = useState(() => intent?.skuId ?? '');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(intent?.skuId ? [intent.skuId] : []));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(readStored<string[]>(HIDDEN_COLUMNS_KEY, DEFAULT_HIDDEN_COLUMNS)));

  function setHiddenPersist(next: Set<string>) {
    setHidden(next);
    writeStored(HIDDEN_COLUMNS_KEY, [...next]);
  }

  const selection = parseNode(node);
  const showPrice = can('inv.price.read');
  const canDemand = can('inv.demand.create');

  const { data: stores } = useStores();
  const viewedStore = stores?.find((s: StoreRow) => s.id === selection.storeId);
  const storeHasBins = (viewedStore?.binCount ?? 0) > 0;
  const binsView = selection.view === 'bins';
  // The bin lives in the navigator's node id, so the tree and the Bin filter
  // always agree, and switching store never keeps another store's bin.
  const binId = binsView ? selection.binId : '';
  const lowView = selection.view === 'low';
  // With every store in view, an item held in sixteen stores would be sixteen
  // rows. It is one instead, with the totals, and opens to show its stores.
  // Running short across all stores is judged the same way, per item, so the
  // list is exactly the items the dashboards count.
  const consolidated = selection.storeId === null && (selection.view === 'all' || lowView);
  // Every bin in one request, to list them by name under each store's node.
  const { data: allBins } = useQuery({
    queryKey: ['storage-bins', 'all'],
    queryFn: () => api.get('/api/v1/storage-bins'),
    enabled: (stores ?? []).some((s: StoreRow) => s.binCount > 0),
  });
  const { data: categories } = useQuery({
    queryKey: ['inv', 'categories', false],
    queryFn: () => api.get('/api/v1/inventory/categories'),
    enabled: can('inv.sku.read'),
  });

  const query = {
    search: search || undefined,
    ...farmScopeQuery(farmIds),
    storeId: selection.storeId ?? undefined,
    categoryId: categoryId || undefined,
    belowReorder: lowView ? 'true' : undefined,
    groupBy: binsView ? 'bin' : selection.view === 'lots' ? 'lot' : consolidated ? 'sku' : 'item',
    binId: binId || undefined,
    skuId: skuFilter || undefined,
    take: PAGE_SIZE,
    skip,
  };

  const balances = useQuery({
    queryKey: ['inv', 'balances', query],
    queryFn: () => api.get('/api/v1/inventory/balances', query),
  });

  const rows = balances.data?.items ?? [];
  // The item asked for, as the list names it, for the filter's chip.
  const filteredItem = skuFilter ? rows.find((r) => r.skuId === skuFilter) : undefined;

  // Open demands by store and item, so a low row that has already been asked
  // for says so instead of offering to ask again.
  const openDemands = useQuery({
    queryKey: ['inv', 'demands', 'open'],
    queryFn: () => api.get('/api/v1/inventory/demands/open'),
    enabled: can('inv.demand.read'),
  });
  const demandFor = new Map((openDemands.data ?? []).map((d) => [`${d.storeId}:${d.skuId}`, d]));

  // An item's stores, fetched once it is opened or ticked: a ticked item
  // stands for all its stores, so they are needed to act on it even closed.
  // Only items the list shows as a group have stores to fetch.
  const groupSkus = new Set(rows.filter(isGroupRow).map((r) => r.skuId));
  const expandedSkus = consolidated
    ? [...new Set([...expanded, ...[...checked].map(skuOfKey)])].filter((skuId) => groupSkus.has(skuId))
    : [];
  const storeQueries = useQueries({
    queries: expandedSkus.map((skuId) => {
      const q = {
        skuId,
        groupBy: 'item',
        categoryId: categoryId || undefined,
        ...farmScopeQuery(farmIds),
        take: 200,
        skip: 0,
      };
      return { queryKey: ['inv', 'balances', q], queryFn: () => api.get('/api/v1/inventory/balances', q) };
    }),
  });
  const storesOf = new Map(expandedSkus.map((skuId, i) => [skuId, storeQueries[i]]));
  const childIds = new Set<string>();
  // The grid's rows: each opened item followed by its stores. The page is
  // still the server's page of items, so paging and totals stay per item.
  const displayRows = consolidated
    ? rows.flatMap((r) => {
        if (!isGroupRow(r) || !expanded.has(r.skuId)) return [r];
        const children = storesOf.get(r.skuId)?.data?.items ?? [];
        for (const c of children) childIds.add(c.id);
        return [r, ...children];
      })
    : rows;

  function toggleExpand(row: StockRow) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.skuId)) next.delete(row.skuId);
      else next.add(row.skuId);
      return next;
    });
  }

  // The navigator's counts come from the store summaries, which already
  // carry them — no extra query per node.
  const dashboard = useQuery({
    queryKey: ['inv', 'dashboard', farmIds],
    queryFn: () => api.get('/api/v1/inventory/dashboard', farmScopeQuery(farmIds)),
    enabled: can('inv.dashboard.read'),
  });
  const belowThreshold = dashboard.data?.belowThreshold;

  const navNodes = useMemo<NavNode[]>(() => {
    const visible = (stores ?? []).filter(
      (s: StoreRow) => farmIds.length === 0 || (s.farmId !== null && farmIds.includes(s.farmId)) || s.type === 'CENTRAL',
    );
    return [
      { id: 'all', label: 'All stores', hint: 'Every store you can use', icon: <AppIcon kind="store" /> },
      {
        id: 'low',
        label: 'Running short',
        hint: 'Below the reorder level, all stores together',
        count: belowThreshold,
        icon: <AppIcon kind="attention" />,
      },
      ...visible.map((s: StoreRow) => ({
        id: `store:${s.id}:all`,
        label: `${s.code} — ${s.nameEn}`,
        count: s.itemsInStock,
        hint: s.type === 'CENTRAL' ? 'Main store' : (s.farmName ?? undefined),
        icon: <AppIcon kind={s.type === 'CENTRAL' ? 'centralStore' : 'store'} />,
        // The store row itself shows everything in it, so there is no
        // "Everything" child: it shared the store row's id, lit up alongside
        // it, and trapped the arrow keys on that store.
        children: [
          { id: `store:${s.id}:low`, label: 'Running short', count: s.belowReorderCount, icon: <AppIcon kind="attention" /> },
          { id: `store:${s.id}:lots`, label: 'Lots', icon: <AppIcon kind="lots" /> },
          // Bins are optional: only a store that has them gets the node. Each
          // bin is listed under it by name, so one click shows what it holds.
          ...(s.binCount > 0
            ? [
                {
                  id: binNode(s.id, ''),
                  label: 'Bins',
                  count: s.binCount,
                  icon: <AppIcon kind="bin" />,
                  children: [
                    ...(allBins ?? [])
                      .filter((b) => b.storeId === s.id)
                      .map((b) => ({
                        id: binNode(s.id, b.id),
                        label: b.nameEn,
                        hint: b.code,
                        count: b.itemsInStock,
                        icon: <AppIcon kind="bin" />,
                      })),
                    { id: binNode(s.id, 'none'), label: 'Loose (in no bin)', icon: <AppIcon kind="bin" /> },
                  ],
                },
              ]
            : []),
        ],
      })),
    ];
  }, [stores, farmIds, allBins, belowThreshold]);

  /** Every way of changing the node starts the list afresh. */
  function goTo(id: string) {
    setNode(id);
    setSkip(0);
    setSelectedKey(null);
    setChecked(new Set());
    // One item asked for stays open wherever the tree goes.
    setExpanded(new Set(skuFilter ? [skuFilter] : []));
  }

  function clearSkuFilter() {
    setSkuFilter('');
    setSkip(0);
    setChecked(new Set());
    setExpanded(new Set());
  }

  // The action opens on this screen, prefilled from the row, rather than
  // sending the user to another tab to finish the job.
  function openStockAction(kind: StockActionKind, row: StockRow) {
    if (!row.skuId || !row.storeId) return;
    setStockAction({ kind, row });
  }

  function rowActions(row: StockRow): DesktopAction[] {
    // Every action works on one store's stock, and an item's All stores row
    // is not one store — the actions stay in place, disabled, saying why.
    if (isGroupRow(row)) {
      return [
        {
          label: expanded.has(row.skuId) ? 'Hide its stores' : 'Show its stores', icon: 'store',
          shortcut: 'Enter',
          onSelect: () => toggleExpand(row),
          allowed: true,
        },
        ...storeActions(row)
          .slice(1)
          .map((a) => ({ ...a, enabled: false, reason: 'Expand and choose a store' })),
      ];
    }
    return storeActions(row);
  }

  function storeActions(row: StockRow): DesktopAction[] {
    const ids = Boolean(row.skuId && row.storeId);
    return [
      {
        label: 'Open', icon: 'stockCard',
        shortcut: 'Enter',
        onSelect: () => openItem(row),
        allowed: true,
      },
      {
        label: 'Receive (Stock In)', icon: 'inbound',
        onSelect: () => openStockAction('receive', row),
        allowed: can('inv.inward.create'),
        enabled: ids,
        reason: 'The server needs an update before this works',
        separatorBefore: true,
      },
      {
        label: 'Issue (Stock Out)', icon: 'outbound',
        onSelect: () => openStockAction('issue', row),
        allowed: can('inv.consumption.create'),
        enabled: ids,
        reason: 'The server needs an update before this works',
      },
      {
        label: 'Transfer', icon: 'transfer',
        onSelect: () => openStockAction('transfer', row),
        allowed: can('inv.transfer.create'),
        enabled: ids,
        reason: 'The server needs an update before this works',
      },
      {
        label: 'Adjust', icon: 'adjust',
        onSelect: () => openStockAction('adjust', row),
        allowed: can('inv.adjustment.create'),
        enabled: ids,
        reason: 'The server needs an update before this works',
      },
      {
        label: 'Demand stock', icon: 'demand',
        onSelect: () => setDemandRow(row),
        allowed: canDemand,
        enabled: ids && !demandFor.has(`${row.storeId}:${row.skuId}`),
        reason: ids ? 'A demand for this item here is already open' : 'The server needs an update before this works',
        separatorBefore: true,
      },
    ];
  }

  // Ticks in the All stores view: `sku:<item>` ticks an item's group, all its
  // stores, open or closed; `<store>:<item>` ticks one store's row. Kept in
  // one shape — a group fully ticked is its own key, never all its stores'.
  const storeKeysOf = (skuId: string) => (storesOf.get(skuId)?.data?.items ?? []).map((c) => c.id);
  /** A store row shown under a group, as opposed to an item held in one store only. */
  const underGroup = (key: string) => !isGroupKey(key) && groupSkus.has(skuOfKey(key));

  function groupState(skuId: string, set: ReadonlySet<string> = checked): 'all' | 'some' | 'none' {
    if (set.has(`sku:${skuId}`)) return 'all';
    const keys = storeKeysOf(skuId);
    const ticked = keys.filter((k) => set.has(k)).length;
    if (ticked === 0) return 'none';
    return ticked === keys.length ? 'all' : 'some';
  }

  function tidy(set: Set<string>): Set<string> {
    for (const skuId of groupSkus) {
      const keys = storeKeysOf(skuId);
      if (set.has(`sku:${skuId}`) || (keys.length > 0 && keys.every((k) => set.has(k)))) {
        for (const k of keys) set.delete(k);
        set.add(`sku:${skuId}`);
      }
    }
    return set;
  }

  function toggleChecked(key: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      const skuId = skuOfKey(key);
      if (isGroupKey(key)) {
        for (const k of storeKeysOf(skuId)) next.delete(k);
        if (groupState(skuId, prev) === 'all') next.delete(key);
        else next.add(key);
      } else if (underGroup(key) && next.has(`sku:${skuId}`)) {
        // Unticking one store of a ticked item leaves the rest of its stores.
        next.delete(`sku:${skuId}`);
        for (const k of storeKeysOf(skuId)) if (k !== key) next.add(k);
      } else if (next.has(key)) next.delete(key);
      else next.add(key);
      return tidy(next);
    });
  }

  /** The rows the list shows at the top level: items, and items held in one store. */
  const topRows = displayRows.filter((r) => !childIds.has(r.id));
  const allTicked =
    topRows.length > 0 && topRows.every((r) => (isGroupRow(r) ? groupState(r.skuId) === 'all' : checked.has(r.id)));
  // Everything on the page, closed items too: each closed item's stores come with it.
  function toggleCheckAll() {
    setChecked(allTicked ? new Set() : new Set(topRows.map((r) => r.id)));
  }
  function selectRange(keys: string[]) {
    setChecked(tidy(new Set(keys)));
  }

  // What the grid shows ticked: an open item's stores under a ticked item
  // too, and an item whose every store is ticked.
  const shownChecked = new Set(
    displayRows
      .filter((r) =>
        isGroupRow(r)
          ? groupState(r.skuId) === 'all'
          : checked.has(r.id) || (underGroup(r.id) && checked.has(`sku:${r.skuId}`)),
      )
      .map((r) => r.id),
  );

  // The stores' rows to act on, in the list's order; a tick on a row no
  // longer listed counts for nothing. A ticked item whose stores have not
  // come yet holds the bulk actions back until they have, rather than leave
  // them out.
  const checkedRows: StockRow[] = [];
  const waitingFor: StockRow[] = [];
  for (const r of topRows) {
    if (!isGroupRow(r)) {
      if (checked.has(r.id)) checkedRows.push(r);
      continue;
    }
    const stores = storesOf.get(r.skuId)?.data?.items;
    if (checked.has(r.id)) {
      if (stores) checkedRows.push(...stores);
      else waitingFor.push(r);
    } else {
      checkedRows.push(...(stores ?? []).filter((c) => checked.has(c.id)));
    }
  }
  const tickedCount = checkedRows.length + waitingFor.reduce((n, g) => n + (g.storeCount ?? 0), 0);
  const notOnThisPc = waitingFor.some((g) => storesOf.get(g.skuId)?.fetchStatus === 'paused');
  const bulkMode = !binsView && waitingFor.length === 0 && checkedRows.length >= 2;

  // Several items at once. A single item is opened and worked on its own
  // screen, so with fewer than two ticked these stay in place, saying how.
  const tickFirst = waitingFor.length
    ? notOnThisPc
      ? 'The stores of a ticked item are not loaded on this PC'
      : 'Loading the stores of the ticked items…'
    : 'Tick two or more items to work on them together';
  const toolbarActions: DesktopAction[] = [
    {
      label: 'Issue (Stock Out)', icon: 'outbound',
      onSelect: () => setBulkAction('issue'),
      allowed: can('inv.consumption.create'),
      enabled: bulkMode,
      reason: tickFirst,
    },
    {
      label: 'Transfer', icon: 'transfer',
      onSelect: () => setBulkAction('transfer'),
      allowed: can('inv.transfer.create'),
      enabled: bulkMode,
      reason: tickFirst,
    },
    {
      label: 'Adjust', icon: 'adjust',
      onSelect: () => setBulkAction('adjust'),
      allowed: can('inv.adjustment.create'),
      enabled: bulkMode,
      reason: tickFirst,
    },
  ];

  // Quantities in packages ("2 Cartons + 38 Packs") or by weight ("6,900
  // KG"), for items that have either. The column only shows when one does.
  const items = useItemsById();
  const packedOf = (r: StockRow) => {
    const item = items.get(r.skuId);
    return item ? packedText(r.qty, item) : null;
  };
  const anyPacked = rows.some((r) => packedOf(r) !== null);

  const columns: Array<GridColumn<StockRow>> = [
    { key: 'skuCode', header: 'Code', width: 120, render: (r) => <span className="mono">{r.skuCode}</span> },
    { key: 'skuName', header: 'Item', width: 260, render: (r) => r.skuName },
    {
      key: 'bin',
      header: 'Bin',
      width: 170,
      render: (r: StockRow) =>
        binsView ? (
          r.binCode ? (
            <span className="withicon">
              <AppIcon kind="bin" size={13} />
              <span className="mono">{r.binCode}</span> {r.binName}
            </span>
          ) : (
            <span className="dim">Loose</span>
          )
        ) : r.bins?.length ? (
          <span className="withicon">
            <AppIcon kind="bin" size={13} />
            <span className="mono">{r.bins.join(', ')}</span>
          </span>
        ) : (
          <span className="dim">—</span>
        ),
    },
    ...(selection.storeId
      ? []
      : [
          {
            key: 'store',
            header: 'Store',
            width: 120,
            render: (r: StockRow) =>
              isGroupRow(r) ? (
                expanded.has(r.skuId) && storesOf.get(r.skuId)?.isPending ? (
                  <span className="dim">{storesOf.get(r.skuId)?.fetchStatus === 'paused' ? 'Not loaded on this PC' : 'Loading…'}</span>
                ) : (
                  <span className="dim">
                    {r.storeCount ?? 0} {r.storeCount === 1 ? 'store' : 'stores'}
                  </span>
                )
              ) : (
                <span className="dim">{r.storeCode}</span>
              ),
          },
        ]),
    selection.view === 'lots' || binsView
      ? {
          key: 'lot',
          header: 'Lot',
          width: 160,
          render: (r: StockRow) => (
            <span className="dim">
              {r.batchNo ? <span className="mono">{r.batchNo}</span> : '—'}
              {r.expiryDate && <> · exp {shortDate(r.expiryDate)}</>}
            </span>
          ),
        }
      : {
          key: 'lots',
          header: 'Lots',
          width: 90,
          align: 'end' as const,
          render: (r: StockRow) => <span className="dim">{r.lotCount ?? '—'}</span>,
        },
    { key: 'qty', header: 'Quantity', align: 'end', width: 110, render: (r) => quantity(r.qty, r.uomPrecision) },
    { key: 'uom', header: 'Unit', width: 80, render: (r) => <span className="dim">{r.uom}</span> },
    ...(anyPacked
      ? [{ key: 'packed', header: 'In packages', width: 200, render: (r: StockRow) => <span className="dim">{packedOf(r) ?? ''}</span> }]
      : []),
    ...(showPrice
      ? [
          { key: 'avgCost', header: 'Avg cost', align: 'end' as const, width: 110, render: (r: StockRow) => rate(r.avgCost) },
          { key: 'value', header: 'Value', align: 'end' as const, width: 130, render: (r: StockRow) => money(r.value) },
        ]
      : []),
    {
      key: 'status',
      header: 'Status',
      width: 210,
      render: (r) =>
        isGroupRow(r) ? (
          // Low when any one store is: a healthy total can hide a store that
          // has run dry. Opening the row shows which, each with Demand stock.
          r.belowThreshold ? (
            <span className="pill pill--alert">
              <AppIcon kind="attention" size={12} />
              {isOutOfStock(r)
                ? 'Out of stock'
                : `Low in ${r.lowStoreCount ?? 0} of ${r.storeCount ?? 0} ${r.storeCount === 1 ? 'store' : 'stores'}`}
            </span>
          ) : (
            <span className="pill">
              <AppIcon kind="statusDone" size={12} />
              OK
            </span>
          )
        ) : r.belowThreshold ? (
          <span className="grid__statuscell">
            <span className="pill pill--alert">
              <AppIcon kind="attention" size={12} />
              {isOutOfStock(r) ? 'Out of stock' : 'Low stock'}
            </span>
            {/* The whole point of flagging a row is refilling it, so asking
                for more is one click away here. A row already asked for
                shows its open demand instead of offering a second one. */}
            {demandFor.has(`${r.storeId}:${r.skuId}`) ? (
              <span className="pill" title="Waiting on the Stock Demands screen">
                <AppIcon kind="demand" size={12} />
                Demanded · {demandFor.get(`${r.storeId}:${r.skuId}`)!.documentNo}
              </span>
            ) : (
              canDemand &&
              Boolean(r.skuId && r.storeId) && (
                <button
                  type="button"
                  className="btn btn--sm btn--withicon grid__rowbtn"
                  aria-label={`Demand stock — ${r.skuCode} in ${r.storeCode}`}
                  title="Ask for more of this item in this store"
                  onClick={(e) => {
                    // A plain row click replaces the checked set; pressing a
                    // row's own button must not throw away a bulk selection.
                    e.stopPropagation();
                    setDemandRow(r);
                  }}
                >
                  <AppIcon kind="demand" size={13} />
                  Demand stock
                </button>
              )
            )}
          </span>
        ) : (
          <span className="pill">
            <AppIcon kind="statusDone" size={12} />
            OK
          </span>
        ),
    },
  ];

  const columnOptions: ColumnOption[] = columns.map((c) => ({ key: c.key, label: c.header }));
  const visibleColumns = columns.filter((c) => !hidden.has(c.key));

  /** One store's stock of an item on its own screen. An All stores row has none: it opens in place instead. */
  function openItem(row: StockRow) {
    if (isGroupRow(row)) {
      toggleExpand(row);
      return;
    }
    setSelectedKey(row.id);
    setOpenRow(row);
    setNote(null);
    setPageTab(can('inv.ledger.read') ? 'stockcard' : 'lots');
  }

  const grid = (
    <div className="workbench__main">
      <div className="filterbar">
        <SearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setSkip(0);
            setExpanded(new Set());
            setChecked(new Set());
          }}
          placeholder="Search items by code or name…"
        />
        {categories && (
          <Select
            label="Category"
            value={categoryId}
            onChange={(v) => {
              setCategoryId(v);
              setSkip(0);
              setExpanded(new Set());
              setChecked(new Set());
            }}
            placeholder="All categories"
            options={categories.map((c) => ({ value: c.id, label: c.nameEn }))}
            // A blank hint, same as View's, so both fields reserve the same
            // height and their boxes stay on one line instead of View's
            // hint line pushing it out of alignment with Category.
            hint=" "
          />
        )}
        <Select
          label="View"
          value={selection.view === 'lots' || binsView ? selection.view : 'items'}
          onChange={(v) => {
            const store = selection.storeId;
            setNode(store ? `store:${store}:${v === 'lots' || v === 'bins' ? v : 'all'}` : 'all');
            setSkip(0);
            // A lot's row and an item's are different rows: ticks don't carry over.
            setChecked(new Set());
          }}
          placeholder=""
          options={[
            { value: 'items', label: 'By item' },
            { value: 'lots', label: 'By lot' },
            ...(storeHasBins || binsView ? [{ value: 'bins', label: 'By bin' }] : []),
          ]}
          hint={selection.storeId ? ' ' : 'Choose a store to see lots'}
        />
        {binsView && (
          <BinSelect
            storeId={selection.storeId ?? undefined}
            value={binId}
            onChange={(value) => {
              if (selection.storeId) setNode(binNode(selection.storeId, value));
              setSkip(0);
            }}
            placeholder="All bins"
            looseOption="Loose (in no bin)"
            hint={' '}
          />
        )}
        {/* Arriving from the dashboard's Running short card lands here; the
            way back to everything is beside the filters, not only in the tree. */}
        {skuFilter && (
          <span className="filterchip">
            <AppIcon kind="item" size={13} />
            Only {filteredItem ? `${filteredItem.skuCode} ${filteredItem.skuName}` : (intent?.skuLabel ?? 'one item')}
            <button type="button" aria-label="Show every item" title="Show every item" onClick={clearSkuFilter}>
              Show all items ✕
            </button>
          </span>
        )}
        {lowView && (
          <span className="filterchip">
            <AppIcon kind="attention" size={13} />
            Only items running short{viewedStore ? ` in ${viewedStore.code}` : ''}
            <button
              type="button"
              aria-label="Show all stock"
              title="Show all stock"
              onClick={() => goTo(stockNode(selection.storeId, 'all'))}
            >
              Show all ✕
            </button>
          </span>
        )}
      </div>

      <ActionBar actions={toolbarActions}>
        {tickedCount > 0 && (
          <>
            <span className="actionbar__count">{tickedCount} selected</span>
            <button type="button" className="btn btn--ghost btn--sm btn--withicon" onClick={() => setChecked(new Set())}>
              <AppIcon kind="clear" size={15} />
              Clear selection
            </button>
          </>
        )}
        <ColumnToggle columns={columnOptions} hidden={hidden} onChange={setHiddenPersist} />
      </ActionBar>

      {balances.isError && <ErrorNote message={balances.error.message} />}

      <DataGrid
        id="workbench-stock"
        label="Stock"
        columns={visibleColumns}
        rows={displayRows}
        rowKey={(r) => r.id}
        selectedKey={selectedKey}
        onSelect={(key) => setSelectedKey(key)}
        // One tap opens a store's stock on its own screen. An item's All
        // stores row opens in place instead: it has no stock card of its
        // own, its stores do.
        onOpen={openItem}
        openOnClick
        actions={rowActions}
        loading={balances.isPending}
        empty={
          // A failed load is not an empty store: the note above says what went wrong.
          balances.isError
            ? 'Nothing to show until the stock can be read.'
            : skuFilter && !search && !categoryId
            ? 'None of this item is held here right now.'
            : search || categoryId
            ? 'No items match your search.'
            : lowView
              ? 'Nothing is running short. Every item is at or above its reorder level.'
              : binId
                ? 'Nothing is in this bin right now.'
                : 'This store has no stock yet.'
        }
        // One item can sit in several bins, so a bin view's rows are not one
        // per item and lot — the bulk actions, which are, stay off here.
        {...(binsView
          ? {}
          : {
              selectedKeys: shownChecked,
              onToggleSelect: toggleChecked,
              onToggleSelectAll: toggleCheckAll,
              onSelectRange: selectRange,
            })}
        {...(consolidated
          ? {
              rowDepth: (r: StockRow) => (childIds.has(r.id) ? 1 : 0),
              rowExpanded: (r: StockRow) => (isGroupRow(r) ? expanded.has(r.skuId) : undefined),
              onToggleExpand: toggleExpand,
              rowPartlySelected: (r: StockRow) => isGroupRow(r) && groupState(r.skuId) === 'some',
            }
          : {})}
      />

      <Pager
        skip={skip}
        take={PAGE_SIZE}
        total={balances.data?.total ?? 0}
        onChange={(next) => {
          setSkip(next);
          setExpanded(new Set());
          // Ticks are for rows on screen: another page starts with none.
          setChecked(new Set());
        }}
      />
    </div>
  );

  // Whatever was opened from the stock card, the dialogs open over it.
  const dialogs = (
    <>
        {stockAction && (
          <StockActionDialog
            action={stockAction.kind}
            row={stockAction.row}
            onClose={() => setStockAction(null)}
            onSaved={(message) => {
              setStockAction(null);
              setNote(message);
            }}
          />
        )}

        {demandRow && (
          <DemandDialog
            subject={demandRow}
            onClose={() => setDemandRow(null)}
            onSaved={(message) => {
              setDemandRow(null);
              setNote(message);
            }}
          />
        )}

        {bulkAction && checkedRows.length > 0 && (
          <BulkActionDialog
            action={bulkAction}
            rows={checkedRows}
            onClose={() => setBulkAction(null)}
            onSaved={(message) => {
              setBulkAction(null);
              setChecked(new Set());
              setNote(message);
            }}
          />
        )}
    </>
  );

  if (openRow) {
    // Fresh from the list while it is still on it: a receipt made from this
    // screen shows in its tiles as soon as the list has reloaded.
    const live = displayRows.find((r) => r.id === openRow.id) ?? openRow;
    const actions = storeActions(live).filter((a) => a.label !== 'Open' && a.allowed);
    const demanded = demandFor.get(`${live.storeId}:${live.skuId}`);
    return (
      <>
        <StockItemPage
          row={live}
          showPrice={showPrice}
          canSeeLedger={can('inv.ledger.read')}
          tab={pageTab}
          onTab={setPageTab}
          demanded={demanded}
          onBack={() => {
            setOpenRow(null);
            setNote(null);
          }}
          actions={actions}
          note={note}
        />
        {dialogs}
      </>
    );
  }

  return (
    <div className="workbench workbench--stock">
      {dashboard.data && (
        <div className="workbench__tiles">
          {showPrice && (
            <span className="worktile">
              <span className="worktile__label withicon">
              <AppIcon kind="value" size={14} />
              Stock value
            </span>
              <strong>{money(dashboard.data.stockValue)}</strong>
            </span>
          )}
          <span className="worktile">
            <span className="worktile__label withicon">
              <AppIcon kind="item" size={14} />
              Items tracked
            </span>
            <strong>{dashboard.data.skusTracked}</strong>
          </span>
          {/* All stores together, as the dashboards count it: pressing it
              lists exactly those items, pressing again shows everything. */}
          <button
            type="button"
            className={`worktile worktile--action${dashboard.data.belowThreshold > 0 ? ' worktile--alert' : ''}`}
            aria-pressed={node === 'low'}
            title={node === 'low' ? 'Show all stock' : 'Show only the items running short'}
            onClick={() => goTo(node === 'low' ? 'all' : 'low')}
          >
            <span className="worktile__label withicon">
              <AppIcon kind="attention" size={14} />
              Running short
            </span>
            <strong>{dashboard.data.belowThreshold}</strong>
          </button>
          <span className={`worktile${dashboard.data.expiringSoon > 0 ? ' worktile--warn' : ''}`}>
            <span className="worktile__label withicon">
              <AppIcon kind="expiring" size={14} />
              Expiring soon
            </span>
            <strong>{dashboard.data.expiringSoon}</strong>
          </span>
        </div>
      )}

      <SplitView
        id="workbench"
        initial={240}
        firstLabel="store list"
        first={
          <Navigator
            id="workbench"
            label="Stores"
            nodes={navNodes}
            selectedId={node}
            onSelect={goTo}
          />
        }
        second={grid}
      />

      {dialogs}

      <StatusBar>
        <span>
          {rows.length} of {balances.data?.total ?? 0} rows
        </span>
        <span>{tickedCount > 0 ? `${tickedCount} ticked` : 'Tap an item to open it'}</span>
        {showPrice && (
          <span>
            Value on screen:{' '}
            {money(rows.reduce((sum, r) => sum + Number(r.value ?? 0), 0).toFixed(2))}
          </span>
        )}
        {note && (
          <span className="statusbar__note">
            {note}
            <button type="button" aria-label="Dismiss" onClick={() => setNote(null)}>
              ✕
            </button>
          </span>
        )}
      </StatusBar>
    </div>
  );
}

/**
 * One store's stock of one item on its own screen: how much is there and what
 * it is worth, then its stock card, its lots, and who else holds it. Every
 * stock action on it is one press away at the top.
 */
function StockItemPage({
  row,
  showPrice,
  canSeeLedger,
  tab,
  onTab,
  demanded,
  onBack,
  actions,
  note,
}: {
  row: StockRow;
  showPrice: boolean;
  canSeeLedger: boolean;
  tab: PageTab;
  onTab: (tab: PageTab) => void;
  demanded: { id: string; documentNo: string } | undefined;
  onBack: () => void;
  actions: DesktopAction[];
  note: string | null;
}) {
  const navigate = useShellNav((s) => s.navigate);
  const packed = useItemsById().get(row.skuId);
  // The two repos deploy separately, so a web build can meet an older API
  // whose stock rows carry no item id. Say that plainly instead of passing
  // "undefined" to the next request and showing its validation error.
  const hasIds = Boolean(row.skuId && row.storeId);
  const tabs: Array<{ id: PageTab; label: string }> = [
    ...(canSeeLedger ? [{ id: 'stockcard' as const, label: 'Stock card' }] : []),
    { id: 'lots', label: 'Lots' },
    { id: 'elsewhere', label: 'Other stores' },
  ];
  const shown = tabs.some((t) => t.id === tab) ? tab : tabs[0]!.id;
  // A lot or bin row is part of the store's stock: said, so its quantity is not read as the whole.
  const part = row.binCode
    ? `bin ${row.binCode}`
    : row.binId === null && row.binCode === null && row.binName === null
      ? 'loose, in no bin'
      : row.batchNo
        ? `lot ${row.batchNo}${row.expiryDate ? ` · expires ${shortDate(row.expiryDate)}` : ''}`
        : null;

  return (
    <DocumentFrame
      onBack={onBack}
      backLabel="Back to Stock"
      title={
        <>
          <h2 className="withicon">
            <AppIcon kind="item" size={18} />
            <span>
              <span className="mono">{row.skuCode}</span> {row.skuName}
            </span>
          </h2>
          <span className="pill">
            <AppIcon kind="store" size={12} />
            {row.storeCode}
          </span>
          {part && <span className="muted">{part}</span>}
        </>
      }
      actions={actions.map((a) => (
        <button
          key={a.label}
          type="button"
          className="btn btn--withicon"
          disabled={a.enabled === false}
          title={a.enabled === false ? a.reason : undefined}
          onClick={a.onSelect}
        >
          {a.icon && <AppIcon kind={a.icon} size={15} />}
          {a.label}
        </button>
      ))}
    >
      {note && <OkNote message={note} />}
      {row.belowThreshold && (
        <p className="docpage__note docpage__note--alert" role="note">
          {isOutOfStock(row) ? 'Out of stock here.' : 'Running short here: below the reorder level.'}{' '}
          {demanded ? (
            <>
              Already asked for in{' '}
              <button type="button" className="celllink" onClick={() => navigate('inv-demand', { recordId: demanded.id })}>
                {demanded.documentNo}
              </button>
              .
            </>
          ) : (
            'Use Demand stock to ask for more.'
          )}
        </p>
      )}

      <div className="tiles">
        <Tile
          label="On hand"
          value={`${quantity(row.qty, row.uomPrecision)} ${row.uom}`}
          note={(packed && packedText(row.qty, packed)) || undefined}
          tone={isOutOfStock(row) ? 'alert' : row.belowThreshold ? 'warn' : undefined} />
        {showPrice && <Tile label="Avg cost" value={rate(row.avgCost)} />}
        {showPrice && <Tile label="Value" value={money(row.value)} />}
        {row.isBatchTracked && !row.batchNo && row.lotCount !== undefined && <Tile label="Lots" value={String(row.lotCount)} />}
        <Tile label="Last moved" value={row.lastMovementAt ? shortDate(row.lastMovementAt) : '—'} />
      </div>

      <section className="stack docpage__section">
        <div className="viewtoggle" role="tablist" aria-label="About this stock">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={shown === t.id}
              className={`viewtoggle__btn${shown === t.id ? ' viewtoggle__btn--on' : ''}`}
              onClick={() => onTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {!hasIds ? (
          <ErrorNote message="Cannot load this: the server did not send the item id. The server needs an update." />
        ) : shown === 'stockcard' ? (
          <StockCard row={row} showPrice={showPrice} />
        ) : shown === 'lots' ? (
          <LotsTab row={row} showPrice={showPrice} />
        ) : (
          <ElsewhereTab row={row} showPrice={showPrice} />
        )}
      </section>
    </DocumentFrame>
  );
}

const MOVEMENT_LABELS: Record<string, string> = {
  RECEIPT: 'Received',
  ISSUE: 'Issued',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  RETURN_IN: 'Return in',
  RETURN_OUT: 'Return out',
  ADJUSTMENT_IN: 'Adjustment +',
  ADJUSTMENT_OUT: 'Adjustment −',
  OPENING_BALANCE: 'Opening balance',
  REVERSAL: 'Reversal',
};

/** The screens a stock card line's document opens on, by the ledger's source type. */
const SOURCE_SCREENS: Record<string, { screen: string; permission: string; what: string }> = {
  InwardShipment: { screen: 'inv-inward', permission: 'inv.inward.read', what: 'receipt' },
  ConsumptionLog: { screen: 'inv-consume', permission: 'inv.consumption.read', what: 'issue' },
  StockTransfer: { screen: 'inv-xfer', permission: 'inv.transfer.read', what: 'transfer' },
  StockAdjustment: { screen: 'inv-adjust', permission: 'inv.adjustment.read', what: 'adjustment' },
};

/** Every movement of this item in this store, newest first, with the
 *  running balance after each — the paper stock card, from the ledger. */
function StockCard({ row, showPrice }: { row: StockRow; showPrice: boolean }) {
  const navigate = useShellNav((s) => s.navigate);
  const { can } = useAuth();
  // A line's document opens on its own screen, for whoever may see that screen.
  const opens = (m: { sourceType: string; sourceNo: string | null }) => {
    const doc = SOURCE_SCREENS[m.sourceType];
    return doc && m.sourceNo && can(doc.permission) ? doc : undefined;
  };
  const query = {
    skuId: row.skuId,
    storeId: row.storeId,
    batchId: row.batchId ?? undefined,
    order: 'desc' as const,
    take: 100,
    skip: 0,
  };
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['inv', 'ledger', query],
    queryFn: () => api.get('/api/v1/inventory/ledger', query),
  });

  if (isPending) return <LoadingNote />;
  if (isError) return <ErrorNote message={error.message} />;

  return (
    <Table
      count={data.items.length}
      empty="No stock has moved here yet."
      head={
        <tr>
          <th>Date</th>
          <th>Movement</th>
          <th>Document</th>
          <th>Lot</th>
          <th className="num">In</th>
          <th className="num">Out</th>
          <th className="num">Balance</th>
          {showPrice && <th className="num">Value after</th>}
        </tr>
      }
    >
      {data.items.map((m) => {
        const qty = Number(m.qty);
        return (
          <tr key={m.id}>
            <td>{shortDate(m.movementDate)}</td>
            <td>
              {MOVEMENT_LABELS[m.movementType] ?? m.movementType}
              {m.otherStoreCode && (
                <span className="dim">
                  {' '}
                  {m.movementType === 'TRANSFER_OUT' ? '→' : '←'} {m.otherStoreCode}
                </span>
              )}
              {m.reversesId && <span className="pill">reversal</span>}
            </td>
            <td className="mono">
              {opens(m) ? (
                <button
                  type="button"
                  className="celllink"
                  onClick={() => navigate(opens(m)!.screen, { recordId: m.sourceId })}
                  title={`Open this ${opens(m)!.what}`}
                >
                  {m.sourceNo}
                </button>
              ) : (
                (m.sourceNo ?? '—')
              )}
            </td>
            <td className="dim">{m.batchNo ?? '—'}</td>
            <td className="num">{qty > 0 ? quantity(m.qty, row.uomPrecision) : ''}</td>
            <td className="num">{qty < 0 ? quantity(String(-qty), row.uomPrecision) : ''}</td>
            <td className="num">
              <strong>{quantity(m.balanceQty, row.uomPrecision)}</strong>
            </td>
            {showPrice && <td className="num">{money(m.balanceValue)}</td>}
          </tr>
        );
      })}
    </Table>
  );
}

/** The lots of this item in this store: what expires first, and how much. */
function LotsTab({ row, showPrice }: { row: StockRow; showPrice: boolean }) {
  const query = { storeId: row.storeId, skuId: row.skuId, groupBy: 'lot', take: 100, skip: 0 };
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['inv', 'balances', query],
    queryFn: () => api.get('/api/v1/inventory/balances', query),
  });

  if (isPending) return <LoadingNote />;
  if (isError) return <ErrorNote message={error.message} />;

  return (
    <Table
      count={data.items.length}
      empty="This item has no lots."
      head={
        <tr>
          <th>Lot</th>
          <th>Expiry</th>
          <th className="num">Quantity</th>
          {showPrice && <th className="num">Value</th>}
          <th>Last moved</th>
        </tr>
      }
    >
      {data.items.map((l) => (
        <tr key={l.id}>
          <td className="mono">{l.batchNo ?? '—'}</td>
          <td>{l.expiryDate ? shortDate(l.expiryDate) : <span className="dim">—</span>}</td>
          <td className="num">{quantity(l.qty, l.uomPrecision)}</td>
          {showPrice && <td className="num">{money(l.value)}</td>}
          <td className="dim">{l.lastMovementAt ? shortDate(l.lastMovementAt) : '—'}</td>
        </tr>
      ))}
    </Table>
  );
}

/** The same item in every other store the caller can see: the answer to
 *  "does anyone already have some?" before buying more. */
function ElsewhereTab({ row, showPrice }: { row: StockRow; showPrice: boolean }) {
  const query = { skuId: row.skuId, groupBy: 'item', take: 100, skip: 0 };
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['inv', 'balances', query],
    queryFn: () => api.get('/api/v1/inventory/balances', query),
  });

  if (isPending) return <LoadingNote />;
  if (isError) return <ErrorNote message={error.message} />;

  const others = data.items.filter((r) => r.storeId !== row.storeId);

  return (
    <Table
      count={others.length}
      empty="No other store has this item."
      head={
        <tr>
          <th>Store</th>
          <th className="num">Quantity</th>
          {showPrice && <th className="num">Value</th>}
          <th>Status</th>
        </tr>
      }
    >
      {others.map((r) => (
        <tr key={r.id}>
          <td>
            <span className="mono">{r.storeCode}</span> {r.store}
          </td>
          <td className="num">{quantity(r.qty, r.uomPrecision)}</td>
          {showPrice && <td className="num">{money(r.value)}</td>}
          <td>
            {r.belowThreshold ? <span className="pill pill--alert">⚠ Low stock</span> : <span className="pill">OK</span>}
          </td>
        </tr>
      ))}
    </Table>
  );
}
